import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, User, CreditCard, MapPin, CalendarIcon, Clock, Users, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

interface TourBookingState {
  tourId: string;
  tourSlug: string;
  tourTitle: string;
  tourImage: string | null;
  destinationTitle: string;
  pricingMode: string;
  adultPrice: number | null;
  childPrice: number | null;
  adults: number;
  children: number;
  selectedPackageId: string | null;
  selectedPackageName: string | null;
  selectedPackagePrice: number | null;
  excursionDate: string;
  schedule: string | null;
  totalPrice: number;
  duration: string | null;
}

const TourBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as TourBookingState | null;

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pickupPoint, setPickupPoint] = useState("");
  const [comments, setComments] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [activeMethods, setActiveMethods] = useState<{ method: string }[]>([]);

  useEffect(() => {
    supabase
      .from("payment_methods")
      .select("method, is_active")
      .eq("is_active", true)
      .then(({ data }) => {
        const methods = (data as any[]) ?? [];
        setActiveMethods(methods);
        if (methods.length > 0 && !paymentMethod) {
          setPaymentMethod(methods[0].method);
        }
      });
  }, []);

  if (!state) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No booking information found.</p>
          <Button onClick={() => navigate("/tours")}>Browse Tours</Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!fullName || !email || !phone) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setSubmitting(true);

    const { data: booking, error } = await supabase.from("tour_bookings").insert({
      tour_id: state.tourId,
      tour_package_id: state.selectedPackageId || null,
      excursion_date: format(new Date(state.excursionDate), "yyyy-MM-dd"),
      schedule: state.schedule,
      adults: state.adults,
      children: state.children,
      pricing_mode: state.pricingMode,
      total_price: state.totalPrice,
      customer_name: fullName,
      customer_email: email,
      customer_phone: phone,
      pickup_point: pickupPoint || null,
      comments: comments || null,
      payment_method: paymentMethod,
      payment_status: paymentMethod === "cash" ? "unpaid" : "pending",
    } as any).select().single();

    setSubmitting(false);

    if (error) {
      toast.error("Failed to create booking. Please try again.");
      console.error(error);
      return;
    }

    // Send confirmation email for cash bookings
    if (paymentMethod !== "paypal") {
      supabase.functions.invoke("send-tour-booking-email", {
        body: { booking_id: booking.id },
      }).catch((err) => console.error("Tour email send error:", err));
    }

    if (paymentMethod === "paypal") {
      navigate("/payment", {
        state: {
          type: "tour",
          bookingId: booking.id,
          bookingCode: booking.booking_code,
          tourTitle: state.tourTitle,
          tourImage: state.tourImage,
          destinationTitle: state.destinationTitle,
          excursionDate: state.excursionDate,
          schedule: state.schedule,
          adults: state.adults,
          children: state.children,
          pricingMode: state.pricingMode,
          selectedPackageName: state.selectedPackageName,
          totalPrice: state.totalPrice,
          customerName: fullName,
          customerEmail: email,
          customerPhone: phone,
          pickupPoint,
          comments,
          paymentMethod,
        },
      });
    } else {
      toast.success("Booking created successfully!");
      navigate("/tour-confirmation", {
        state: {
          booking,
          tourTitle: state.tourTitle,
          tourImage: state.tourImage,
          destinationTitle: state.destinationTitle,
          duration: state.duration,
          pricingMode: state.pricingMode,
          selectedPackageName: state.selectedPackageName,
          adultPrice: state.adultPrice,
          childPrice: state.childPrice,
        },
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <div className="container mx-auto px-4 py-8 pt-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* LEFT: Form */}
          <div className="lg:col-span-2 space-y-8">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
                Complete Your <span className="text-primary">Tour Booking</span>
              </h1>
            </div>

            {/* Personal Information */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Personal Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label>Full Name *</Label>
                  <Input placeholder="John Doe" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input type="tel" placeholder="+1 809 555 1234" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
            </motion.div>

            {/* Pickup & Comments */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Pickup & Details</h2>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Pickup Point (Hotel/Address)</Label>
                  <Input placeholder="e.g. Hard Rock Hotel, Punta Cana" value={pickupPoint} onChange={(e) => setPickupPoint(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Comments / Special Requests</Label>
                  <Textarea placeholder="Any special requirements..." value={comments} onChange={(e) => setComments(e.target.value)} />
                </div>
              </div>
            </motion.div>

            {/* Payment Method */}
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="p-6 rounded-2xl bg-card border border-border space-y-6">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-primary" />
                <h2 className="font-display text-lg font-bold text-foreground">Payment Method</h2>
              </div>
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod} className="space-y-3">
                {activeMethods.some((m) => m.method === "cash") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="cash" />
                    <div>
                      <p className="font-medium text-foreground">Cash</p>
                      <p className="text-xs text-muted-foreground">Pay at the meeting point</p>
                    </div>
                  </label>
                )}
                {activeMethods.some((m) => m.method === "paypal") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="paypal" />
                    <div>
                      <p className="font-medium text-foreground">PayPal</p>
                      <p className="text-xs text-muted-foreground">Pay securely with PayPal</p>
                    </div>
                  </label>
                )}
                {activeMethods.some((m) => m.method === "stripe") && (
                  <label className="flex items-center gap-3 p-4 rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                    <RadioGroupItem value="stripe" />
                    <div>
                      <p className="font-medium text-foreground">Credit / Debit Card</p>
                      <p className="text-xs text-muted-foreground">Pay securely with Stripe</p>
                    </div>
                  </label>
                )}
              </RadioGroup>

              <Button
                size="lg"
                className="w-full font-bold text-base mt-4"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Processing..." : "Confirm Booking"}
              </Button>
            </motion.div>
          </div>

          {/* RIGHT: Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 p-6 rounded-2xl bg-card border border-border space-y-5">
              <h3 className="font-display text-lg font-bold text-foreground">Booking Summary</h3>

              {state.tourImage && (
                <div className="rounded-xl overflow-hidden">
                  <img src={state.tourImage} alt={state.tourTitle} className="w-full h-36 object-cover" />
                </div>
              )}

              <div>
                <h4 className="font-display font-bold text-foreground">{state.tourTitle}</h4>
                {state.destinationTitle && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                    <MapPin className="w-3.5 h-3.5" /> {state.destinationTitle}
                  </p>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground flex items-center gap-1.5"><CalendarIcon className="w-4 h-4" /> Date</span>
                  <span className="font-medium text-foreground">{format(new Date(state.excursionDate), "PPP")}</span>
                </div>
                {state.schedule && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Clock className="w-4 h-4" /> Schedule</span>
                    <span className="font-medium text-foreground">{state.schedule}</span>
                  </div>
                )}
                {state.duration && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Clock className="w-4 h-4" /> Duration</span>
                    <span className="font-medium text-foreground">{state.duration}</span>
                  </div>
                )}

                {state.pricingMode === "fixed" ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground flex items-center gap-1.5"><Users className="w-4 h-4" /> Adults</span>
                      <span className="font-medium text-foreground">{state.adults} × ${state.adultPrice}</span>
                    </div>
                    {state.children > 0 && (
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground flex items-center gap-1.5"><Users className="w-4 h-4" /> Children</span>
                        <span className="font-medium text-foreground">{state.children} × ${state.childPrice}</span>
                      </div>
                    )}
                  </>
                ) : state.selectedPackageName && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-1.5"><Compass className="w-4 h-4" /> Package</span>
                    <span className="font-medium text-foreground">{state.selectedPackageName}</span>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-border">
                <div className="flex items-end justify-between">
                  <p className="text-muted-foreground text-sm">Total</p>
                  <p className="text-3xl font-display font-bold text-primary">${state.totalPrice}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TourBookingPage;
